import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormSignatureComponent } from '../../../items/form-signature.component';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { I18nService } from '../../../../services/i18n.service';

// 參與者簽名資料介面
export interface ParticipantSignature {
  id: string;
  name: string;
  title?: string;
  department?: string;
  signature: string | null;
  signedAt?: Date;
}

@Component({
  selector: 'app-meeting-signature-collection',
  templateUrl: './meeting-signature-collection.component.html',
  styleUrls: ['./meeting-signature-collection.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, FormSignatureComponent, TranslatePipe]
})
export class MeetingSignatureCollectionComponent implements OnInit, OnChanges {
  @Input() documentId: string = 'meeting_safety_20251015';
  @Input() title: string = '安全宣導會議簽到表';
  @Input() initialParticipants: ParticipantSignature[] = [];  // 新增：支援載入初始參與者資料
  @Output() signaturesUpdated = new EventEmitter<ParticipantSignature[]>();

  // 參與者清單
  participants: ParticipantSignature[] = [];

  // 新參與者資訊
  newParticipant = {
    name: '',
    title: '',
    department: ''
  };

  constructor(private i18nService: I18nService) {}

  ngOnInit(): void {
    // 初始化參與者清單，如果有初始資料則載入
    this.loadInitialParticipants();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當初始參與者資料變更時重新載入
    if (changes['initialParticipants'] && !changes['initialParticipants'].firstChange) {
      this.loadInitialParticipants();
    }
  }

  // 載入初始參與者資料
  private loadInitialParticipants(): void {
    if (this.initialParticipants && this.initialParticipants.length > 0) {
      // 載入現有的參與者資料
      this.participants = [...this.initialParticipants];
    } else {
      // 初始化空的參與者清單
      this.participants = [];
    }
  }

  // 生成唯一ID
  generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // 簽名變更處理
  onSignatureChange(value: string | null, participantId: string): void {
    const index = this.participants.findIndex(p => p.id === participantId);
    if (index !== -1) {
      this.participants[index].signature = value;
      if (value) {
        this.participants[index].signedAt = new Date();
      } else {
        this.participants[index].signedAt = undefined;
      }

      // 通知父元件簽名已更新
      this.signaturesUpdated.emit([...this.participants]);
    }
  }

  // 新增參與者
  addParticipant(): void {
    if (!this.newParticipant.name.trim()) {
      alert(this.i18nService.translate('meeting.signature.collection.name_required'));
      return;
    }

    const participant: ParticipantSignature = {
      id: this.generateId(),
      name: this.newParticipant.name,
      title: this.newParticipant.title || undefined,
      department: this.newParticipant.department || undefined,
      signature: null
    };

    this.participants.push(participant);

    // 重置表單
    this.newParticipant = {
      name: '',
      title: '',
      department: ''
    };

    // 通知父元件簽名已更新
    this.signaturesUpdated.emit([...this.participants]);
  }

  // 刪除參與者
  removeParticipant(participantId: string): void {
    const index = this.participants.findIndex(p => p.id === participantId);
    if (index !== -1) {
      const confirmMessage = this.i18nService.translate('meeting.signature.collection.confirm_remove', { name: this.participants[index].name });
      if (confirm(confirmMessage)) {
        this.participants.splice(index, 1);

        // 通知父元件簽名已更新
        this.signaturesUpdated.emit([...this.participants]);
      }
    }
  }

  // 獲取已簽名人數
  get signedCount(): number {
    return this.participants.filter(p => p.signature !== null).length;
  }

  // 獲取總人數
  get totalCount(): number {
    return this.participants.length;
  }
}
